import React, { useState, useEffect } from "react";
import api from "../../API/axios";
import "./TwoFactorSetup.css";
import { Button, MenuItem, TextField ,Typography} from "@mui/material";

interface TwoFactorSetupProps {
  email: string;
  clientId: string;
  onSetupComplete: () => void;
  onBack: () => void;
  onBackToPassword: () => void;
  showSnackbar?: (message: string, severity: "success" | "error" | "warning" | "info") => void;
}

const TwoFactorSetup = ({
  email,
  clientId,
  onSetupComplete,
  onBack,
  onBackToPassword,
  showSnackbar,
}: TwoFactorSetupProps) => {
  const [step, setStep] = useState("method");
  const [method, setMethod] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (method) {
      setupTwoFactor();
    }
  }, [method]);

  const setupTwoFactor = async () => {
    setIsLoading(true);
    setError("");

    try {
      console.log("setupTwoFactor called with:", { email, clientId, method });
      if (method === "app") {
        // Setup authenticator app - use registration-specific endpoint (consumer doesn't exist yet)
        const response = await api.post(
          "/ConsumerTwoFactor/registration-setup-app",
          {
            email,
            clientId: Number(clientId),
          }
        );
        const qrUrl =
          response.data?.data?.qrCodeUrl || response.data?.qrCodeUrl || "";
        setQrCodeUrl(qrUrl);
      } else if (method === "email") {
        // Send email 2FA code - use registration-specific endpoint (consumer doesn't exist yet)
        await api.post("/ConsumerTwoFactor/registration-send-email-code", {
          email,
          clientId: Number(clientId),
        });
        // Notify user that verification code was sent to their email
        showSnackbar?.("Verification code sent to your email!", "success");
      }
    } catch (err) {
      console.error(err);
      const errorMsg = "Failed to setup 2FA. Please try again.";
      setError(errorMsg);
      showSnackbar?.(errorMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };
const onBackPassword = () => {
   onBackToPassword();
  };
  const verifyAndCompleteRegistration = async () => {
    console.log("verifyAndCompleteRegistration called with:", {
      email,
      clientId,
      verificationCode,
      method,
    });
    setIsLoading(true);
    setError("");

    try {
      // Step 1: Verify the 2FA code
      const verifyEndpoint = method === "app" 
        ? "/ConsumerTwoFactor/registration-verify-app"
        : "/ConsumerTwoFactor/registration-verify-code";
      
      const verifyResponse = await api.post(verifyEndpoint, {
        email,
        clientId: Number(clientId),
        verificationCode,
      });
      
      if (!verifyResponse.data?.success) {
        const errorMsg = verifyResponse.data?.error || "Verification code is invalid or expired. Please try again.";
        setError(errorMsg);
        showSnackbar?.(errorMsg, "error");
        return;
      }
      
      // Step 2: Get pending registration data from localStorage
      const pendingRegKey = `pendingRegistration_${clientId}`;
      const pendingRegStored = localStorage.getItem(pendingRegKey);
      
      if (!pendingRegStored) {
        const errorMsg = "Registration data not found. Please start registration again.";
        setError(errorMsg);
        showSnackbar?.(errorMsg, "error");
        onBack();
        return;
      }
      
      const pendingReg = JSON.parse(pendingRegStored);
      
      // Validate we have all required data
      if (!pendingReg.firstName || !pendingReg.email || !pendingReg.password) {
        const errorMsg = "Incomplete registration data. Please start registration again.";
        setError(errorMsg);
        showSnackbar?.(errorMsg, "error");
        localStorage.removeItem(pendingRegKey);
        onBack();
        return;
      }
      
      // Step 3: Call complete-registration with all data
      const completeResponse = await api.post("/consumer/complete-registration", {
        firstName: pendingReg.firstName,
        surname: pendingReg.surname || "",
        email: pendingReg.email,
        telephone: pendingReg.telephone || "",
        dateOfBirth: pendingReg.dob,
        clientId: Number(clientId),
        password: pendingReg.password,
        twoFactorMethod: method,
        verificationCode: verificationCode,
      });
      
      if (completeResponse.data?.success) {
        const msg = completeResponse.data.message || "Registration completed successfully! Please login.";
        showSnackbar?.(msg, "success");
        
        // Clear pending registration state - registration is now complete
        localStorage.removeItem(pendingRegKey);
        
        onSetupComplete();
      } else {
        const errorMsg = completeResponse.data?.message || "Registration failed. Please try again.";
        setError(errorMsg);
        showSnackbar?.(errorMsg, "error");
      }
    } catch (err: any) {
      console.error(err);
      let errorMsg = "Registration failed. Please try again.";
      if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      }
      setError(errorMsg);
      showSnackbar?.(errorMsg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const renderMethodSelection = () => (
    <div
      style={{
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
      }}
    >
      <h3 style={{ marginBottom: 0, color: "#888" }}>2 Factor Method</h3>
      <div
        style={{
          fontSize: "13px",
          width: "100%",
          textAlign: "center",
          color: "#666",
        }}
      >
        <p>
          To help keep your account secure, please select your preferred method
          of <br />
          two factor authentication(2FA).
        </p>
      </div>
      <div
        style={{
          width: "100%",
          maxWidth: 260,
          display: "flex",
          flexDirection: "column",
          margin: "0 auto",
        }}
      >
        <TextField
          select
          label="2 Factor Method"
          name="method"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          fullWidth
          required
          sx={{
            width: "100%",
            maxWidth: 260,
          }}
        >
          <MenuItem value="email">Email</MenuItem>
          <MenuItem value="app">Authenticator App</MenuItem>
        </TextField>
      </div>

      {/* App Field - only show when app method is selected */}
      {method === "app" && (
        <>
          <div
            style={{
              fontSize: "13px",
              width: "100%",
              textAlign: "center",
              color: "#666",
            }}
          >
            <p>
              {" "}
              <strong>Step 1) </strong>
              Open or download your chosen authentication app
              <br />
              and scan the below QR code
            </p>
          </div>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {qrCodeUrl ? (
              <div>
                <img
                  src={qrCodeUrl}
                  alt="QR Code for 2FA setup"
                  style={{
                    maxWidth: "120px",
                    border: "1px solid #ddd",
                    padding: "10px",
                  }}
                />
              </div>
            ) : (
              <div className="loading">Generating QR code...</div>
            )}
          </div>
          <div
            style={{
              fontSize: "13px",
              width: "100%",
              textAlign: "center",
              color: "#666",
            }}
          >
            <p>
              {" "}
              <strong>Step 2) </strong>
              Enter the verification code from your authenticator
              <br />
              app below
            </p>
          </div>
        </>
      )}
      <div
        style={{
          width: "100%",
          maxWidth: 260,
          display: "flex",
          flexDirection: "column",
          margin: "0 auto",
        }}
      >
        {/* Email Field - only show when email method is selected */}
        <TextField
          type="text"
          label="Verification Code"
          name="verificationCode"
          value={verificationCode}
          onChange={(e) =>
            setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))
          }
          required
          sx={{ mt: 2, mb: 3 }}
        />

        <Button
          type="submit"
          variant="contained"
          size="large"
          onClick={verifyAndCompleteRegistration}
          disabled={verificationCode.length !== 6 || isLoading}
          sx={{
            width: "100%",
            backgroundColor: "var(--color-primary)",
            "&:hover": { backgroundColor: "var(--color-secondary)" },
          }}
        >
          {isLoading ? "Completing Registration..." : "Complete sign up"}
        </Button>
      </div>
  <Typography
            component="p"
            variant="body2"
            onClick={onBackPassword}
            sx={{ 
              cursor: "pointer",
              color: "var(--color-primary)",
              textAlign: "center",
              marginTop: "10px",
              "&:hover": { color: "var(--color-secondary)" }
            }}
          >
            Back to Set password 
          </Typography>
    </div>
  );

  switch (step) {
    case "method":
      return renderMethodSelection();
    default:
      return renderMethodSelection();
  }
};

export default TwoFactorSetup;
